/**
 * Shared abort-signal composition helpers for the Tyndo API layer.
 *
 * Every authenticated fetch path (`apiFetch`, `useApiClient`,
 * `useApiQuery`/`useApiMutation`) needs the same behaviour: abort the request
 * when EITHER a per-request timeout fires OR a caller-supplied signal aborts
 * (TanStack Query's query-cancellation/unmount signal, a manual
 * `AbortController`, etc.). Centralising the logic here keeps the three call
 * sites in lockstep instead of carrying separate copies that can drift.
 */

/**
 * Build an AbortSignal that fires on a timeout OR when a caller-supplied
 * signal aborts.
 *
 * Prefers the native `AbortSignal.any` when available; falls back to a
 * controller bridge for runtimes that predate it.
 */
export const buildTimeoutSignal = (
  timeoutMs: number,
  callerSignal?: AbortSignal | null,
): AbortSignal => {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  if (!callerSignal) return timeoutSignal;
  // AbortSignal.any composes both sources; abort if either fires.
  const anyFn = (AbortSignal as unknown as { any?: (s: AbortSignal[]) => AbortSignal }).any;
  if (typeof anyFn === 'function') {
    return anyFn([timeoutSignal, callerSignal]);
  }
  // Fallback for runtimes without AbortSignal.any: bridge via a controller.
  const controller = new AbortController();
  const onAbort = () => controller.abort();
  if (callerSignal.aborted) controller.abort();
  else callerSignal.addEventListener('abort', onAbort, { once: true });
  if (timeoutSignal.aborted) controller.abort();
  else timeoutSignal.addEventListener('abort', onAbort, { once: true });
  return controller.signal;
};
